import {
  CalendarOutlineIcon,
  ChildrenSolidIcon,
  InfoOutlineIcon,
} from '@ikyucom/design-system-icons-react'
import { useCallback } from 'react'
import { useEnv } from '~/env'
import { format } from '~/lib/date/date'
import { getReservationConfirmUrl } from '~/lib/url'
import { useReserveNavigate } from '~/navigation'
import { css, sva } from '~/styled-system/css'
import { Button } from '~/ui'
import { useAdultCount } from '../../store/childMenu'
import { useSelectedSeat } from '../../store/seat'
import { useChildCount, useVisitDate, useVisitTime } from '../../store/stateMachine/context'
import { useReserveInfo, useRestaurant } from '../../store/stateMachine/state'

export function RestaurantContents() {
  return (
    <div className={css({ spaceY: { base: '1', _pc: '2' } })}>
      <Name />
      <VisitCondition />
      <DupleReservation />
    </div>
  )
}

function Name() {
  const name = useRestaurant()?.name
  if (name === undefined) {
    return null
  }
  return (
    <h1 className={css({ fontSize: 'xl', fontWeight: 'bold', md: { fontSize: '3xl' } })}>{name}</h1>
  )
}

const visitConditionStyle = sva({
  slots: ['container', 'item', 'icon', 'children'],
  base: {
    container: {
      fontSize: 'md',
      position: 'relative',
      _sd: {
        spaceY: '1',
      },
      _pc: {
        display: 'flex',
        alignItems: 'center',
        spaceX: '3',
      },
    },
    item: {
      display: 'flex',
      alignItems: 'center',
      spaceX: '1',
    },
    icon: {
      color: 'primary',
    },
    children: {
      position: 'absolute',
      right: '0',
      bottom: '0',
    },
  },
})

function VisitCondition() {
  const { visitDate, visitTime, childCount, adultCount, childUse, open } = useDetails()
  const classes = visitConditionStyle()
  if (visitDate === undefined || visitTime === undefined || adultCount === 0) {
    return null
  }
  return (
    <div className={classes.container}>
      <div className={classes.item}>
        <CalendarOutlineIcon width={14} height={14} className={classes.icon} />
        <p>
          {format(visitDate, 'M月d日(E)')} {visitTime}
        </p>
      </div>
      <div className={classes.item}>
        <ChildrenSolidIcon width={14} height={14} className={classes.icon} />
        <span>大人{adultCount}名</span>
        {childCount > 0 && <span className="ml-1">子供{childCount}名</span>}
      </div>
      {childUse !== 'OVER_MIDDLE_SCHOOLERS' && (
        <div className={classes.children}>
          <Button size="xs" color="tertiary" onClick={open}>
            お子様のご利用
          </Button>
        </div>
      )}
    </div>
  )
}

function useDetails() {
  const visitDate = useVisitDate()
  const visitTime = useVisitTime()
  const childCount = useChildCount()
  const adultCount = useAdultCount()
  const childUse = useSelectedSeat()?.childUse ?? 'OVER_MIDDLE_SCHOOLERS'
  const navigate = useReserveNavigate()
  const open = useCallback(() => {
    navigate({ type: 'showChildCount' })
  }, [navigate])
  return { visitDate, visitTime, childCount, adultCount, childUse, open }
}

const dupleReservationStyle = sva({
  slots: ['container', 'heading', 'title', 'content'],
  base: {
    container: {
      spaceY: '1',
      border: '[1px solid]',
      borderColor: 'secondary',
      padding: '3',
    },
    heading: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      spaceX: '1',
    },
    title: {
      fontWeight: 'bold',
      color: 'tertiary',
    },
    content: {
      textAlign: 'center',
      fontSize: 'sm',
    },
  },
})

function DupleReservation() {
  const { count, url } = useDupleReservation()
  const classes = dupleReservationStyle()
  if (count === 0) {
    return null
  }
  return (
    <div className={classes.container}>
      <div className={classes.heading}>
        <InfoOutlineIcon className={css({ color: 'tertiary' })} width={13} height={13} />
        <p className={classes.title}>日程・時間をご確認ください</p>
      </div>
      <p className={classes.content}>
        同日・同時間帯に既に{count}件の予約を受付済みです。
        <br />
        ご予約の確認は
        <a href={url} className={css({ color: 'link' })}>
          こちら
        </a>
      </p>
    </div>
  )
}

function useDupleReservation() {
  const count = useReserveInfo()?.duplicateReserveCount ?? 0
  const env = useEnv()
  const url = getReservationConfirmUrl(env)
  return { count, url }
}
